/** Vue 2 не отслеживает `Set` и `Map`, поэтому выбор храним в массивах с отдельным индексом */

/** Инвариант: included и excluded никогда не заполнены одновременно — смена режима чистит второй */
export interface MmSelectionState<TKey> {
  isCommonCheckboxSelected: boolean;
  included: TKey[];
  excluded: TKey[];
}

export interface MmSelectionIndex {
  [key: string]: true;
}

export interface MmSelectionFieldNames {
  included: string;
  excluded: string;
}

export const MM_PRODUCT_SELECTION_FIELDS: MmSelectionFieldNames = {
  included: 'includedSKUsInternalCodes',
  excluded: 'excludedSKUsInternalCodes',
};

export const MM_RESULT_SELECTION_FIELDS: MmSelectionFieldNames = {
  included: 'includedIds',
  excluded: 'excludedIds',
};

export function createSelectionState<TKey>(): MmSelectionState<TKey> {
  return {
    isCommonCheckboxSelected: false,
    included: [],
    excluded: [],
  };
}

export function buildSelectionIndex<TKey>(
  state: MmSelectionState<TKey>,
  keyOf: (item: TKey) => string,
): MmSelectionIndex {
  const source = state.isCommonCheckboxSelected ? state.excluded : state.included;
  const index: MmSelectionIndex = {};

  for (let i = 0; i < source.length; i += 1) {
    index[keyOf(source[i])] = true;
  }

  return index;
}

export function isRowSelected(
  state: MmSelectionState<any>,
  index: MmSelectionIndex,
  rowKey: string,
): boolean {
  if (state.isCommonCheckboxSelected) {
    // Режим «выбрано всё»: строка отмечена, если её нет среди исключений
    return index[rowKey] !== true;
  }

  return index[rowKey] === true;
}

/** Один ключ может прийти на двух страницах: удаляем все совпадения, меняем через `push`/`splice` */
export function toggleRow<TKey>(
  state: MmSelectionState<TKey>,
  index: MmSelectionIndex,
  item: TKey,
  rowKey: string,
  checked: boolean,
  keyOf: (item: TKey) => string,
): void {
  const target = state.isCommonCheckboxSelected ? state.excluded : state.included;
  // В режиме «выбрано всё» галка добавляет в исключения при снятии, в поштучном — при установке
  const shouldContain = state.isCommonCheckboxSelected ? !checked : checked;

  if (shouldContain) {
    if (index[rowKey] !== true) {
      target.push(item);
      index[rowKey] = true;
    }

    return;
  }

  removeAllByKey(target, rowKey, keyOf);
  delete index[rowKey];
}

function removeAllByKey<TKey>(target: TKey[], rowKey: string, keyOf: (item: TKey) => string): void {
  for (let i = target.length - 1; i >= 0; i -= 1) {
    if (keyOf(target[i]) === rowKey) {
      target.splice(i, 1);
    }
  }
}

export function resetSelectionArrays<TKey>(state: MmSelectionState<TKey>): void {
  state.included = [];
  state.excluded = [];
}

export function resetSelection<TKey>(state: MmSelectionState<TKey>): void {
  state.included = [];
  state.excluded = [];
  state.isCommonCheckboxSelected = false;
}

/** total может расходиться с числом различимых товаров, поэтому счётчик прижат к нулю */
export function selectedCount(state: MmSelectionState<any>, total: number): number {
  if (state.isCommonCheckboxSelected) {
    if (!total || total <= 0) {
      return 0;
    }

    const rest = total - state.excluded.length;

    return rest > 0 ? rest : 0;
  }

  /** В поштучном выборе total не участвует: обнуление по нему гасило бы кнопки при непустом выборе */
  return state.included.length;
}

export function hasSelection(state: MmSelectionState<any>, total: number): boolean {
  return selectedCount(state, total) > 0;
}

export function buildSelectCheckBoxesData<TKey>(
  state: MmSelectionState<TKey>,
  fields: MmSelectionFieldNames,
): { [key: string]: any } {
  const payload: { [key: string]: any } = {
    isCommonCheckboxSelected: state.isCommonCheckboxSelected,
  };

  // Оба массива отправляем всегда: пустой массив значим сам по себе, а не «поля нет»
  payload[fields.included] = state.included.slice();
  payload[fields.excluded] = state.excluded.slice();

  return payload;
}

export function isSelectionEmpty(state: MmSelectionState<any>): boolean {
  return (
    state.isCommonCheckboxSelected === false &&
    state.included.length === 0 &&
    state.excluded.length === 0
  );
}
